import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { Search, UserPlus, Check, Loader2 } from 'lucide-react';

const UserSearch = ({ onSendRequest }) => {
    const { fetchWithAuth } = useContext(AuthContext);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sentTo, setSentTo] = useState(new Set());

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (!query || query.length < 2) { setResults([]); return; }
            setLoading(true);
            try {
                const res = await fetchWithAuth(`/api/sniggle/search/?q=${encodeURIComponent(query)}`);
                if (res.ok) setResults(await res.json());
            } catch (err) { console.error('Search failed', err); }
            finally { setLoading(false); }
        }, 400);
        return () => clearTimeout(timer);
    }, [query]);

    const handleSend = async (user) => {
        if (onSendRequest) {
            await onSendRequest(user.username);
            setSentTo(prev => new Set(prev).add(user.username));
        }
    };

    return (
        <div className="w-full max-w-lg mx-auto">
            {/* Privacy Info */}
            <div className="mb-5 p-3.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-2xl flex items-start gap-3">
                <div className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                    <strong>Datenschutz:</strong> Du siehst nur Nutzer, die sich für die Suche freigegeben haben.
                </p>
            </div>

            {/* Search Input */}
            <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Nutzername suchen..."
                    className="w-full pl-11 pr-11 py-3.5 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-plantlix-500/50 focus:border-plantlix-500 outline-none transition-all text-sm"
                />
                {loading && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plantlix-500 animate-spin" />
                )}
            </div>

            {/* Results */}
            {results.length > 0 && (
                <div className="space-y-2">
                    {results.map(user => {
                        const sent = sentTo.has(user.username);
                        return (
                            <div key={user.id} className="flex items-center gap-3 p-3.5 bg-white dark:bg-white/[0.04] rounded-2xl border border-gray-100 dark:border-white/[0.07] hover:border-plantlix-200 dark:hover:border-plantlix-800/40 transition-all">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white font-black flex-shrink-0">
                                    {user.username.charAt(0).toUpperCase()}
                                </div>
                                <span className="flex-1 font-bold text-gray-900 dark:text-white text-sm truncate">{user.username}</span>
                                <button
                                    onClick={() => !sent && handleSend(user)}
                                    disabled={sent}
                                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                        sent
                                            ? 'bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-600 dark:text-plantlix-400 border border-plantlix-200 dark:border-plantlix-800/40 cursor-default'
                                            : 'bg-plantlix-500 hover:bg-plantlix-600 text-white shadow-md shadow-plantlix-500/20 active:scale-95'
                                    }`}
                                >
                                    {sent ? <><Check className="w-3.5 h-3.5" /> Gesendet</> : <><UserPlus className="w-3.5 h-3.5" /> Hinzufügen</>}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {query.length >= 2 && !loading && results.length === 0 && (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                    <div className="text-3xl mb-2 opacity-40">🔍</div>
                    <p className="text-sm font-medium">Kein Nutzer mit "{query}" gefunden.</p>
                </div>
            )}
        </div>
    );
};

export default UserSearch;
